import {
  CallbackPositionProperty,
  CallbackProperty,
  Cartesian3,
  Cartographic,
  Color,
  Entity,
  GoogleMaps,
  HeadingPitchRoll,
  Ion,
  IonGeocodeProviderType,
  SceneTransforms,
  ScreenSpaceEventType,
  Terrain,
  Transforms,
  viewerDragDropMixin,
  Model,
  Plane,
  ImageMaterialProperty,
} from "cesium";
import { environment } from "../../common/env";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Cesium3DTileset,
  CesiumComponentRef,
  ScreenSpaceEvent,
  ScreenSpaceEventHandler,
  Viewer,
} from "resium";
import { Viewer as CesiumViewer, Math as CesiumMath } from "cesium";
import { v4 as uuidv4 } from "uuid";
import { IQrCode } from "../admin/models";
import { POIsV1Service, QrCodesV1Service } from "../../common/services/api/api";
import { POIPanel } from "./POIPanel";
import _ from "lodash";
import { useLocation, useParams } from "react-router-dom";
import QRCode from "qrcode";
import { QRCodePanel } from "./QRCodePanel";
import { ObjectForm } from "../../common/components/resiumMap/objectForm";
import Context from "../../common/components/utils/context";
import { Utils } from "../../common/helpers/utils";

export interface IPOI {
  id: string;
  name?: string;
  latitude: number;
  longitude: number;
  altitude: number;
  heading: number;
  flipX?: number;
  flipY?: number;
  flipZ?: number;
  modelUrl?: string;
  created?: Date;
  updated?: Date;
}

export const MatterportViewer3 = () => {
  Ion.defaultAccessToken = environment.Cesium_Default_Access_Token;
  GoogleMaps.defaultApiKey = environment.Google_Api_Key;
  const POI_SIZE = 10;
  const QR_SIZE = 1;
  const METER_IN_RADIAN = 1 / 111319.9;
  const QR_SPACE = METER_IN_RADIAN * 0.001;

  const context = useContext(Context);

  const ref = useRef<CesiumComponentRef<CesiumViewer>>(null);

  const [allPOI, setAllPOI] = useState<IPOI[] | null>([]);
  const [currentPOI, setCurrentPOI] = useState<IPOI | null>(null);
  const [putPOIEnabled, setPutPOIEnabled] = useState(false);
  const [POIfile, setPOIFile] = useState<File | null>();
  const [isPOISaving, setIsPOISaving] = useState(false);
  const prevPOI = useRef<IPOI | null>(null);
  const prevAllPOI = useRef<IPOI[] | null>(null);

  const [rotatePOIEnabled, setRotatePOIEnabled] = useState(false);

  const [allQR, setAllQR] = useState<IQrCode[] | null>([]);
  const [currentQR, setCurrentQR] = useState<IQrCode | null>(null);
  const [putQREnabled, setPutQREnabled] = useState(false);
  const prevAllQR = useRef<IQrCode[] | null>(null);
  const prevQR = useRef<IQrCode | null>(null);

  const [showPOISavingForm, setShowPOISavingForm] = useState(false);

  const { id } = useParams<{ id: string }>();

  const location = useLocation();
  const isPOI = location.pathname.includes("/poi/");
  const isQRCode = location.pathname.includes("/qr-code/");

  useEffect(() => {
    initMap();
    if (!id) {
      getCurrentPosition();
    }
  }, [ref.current && ref.current.cesiumElement]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isQRCode) {
      if (allQR && allQR.length > 0 && id) {
        const qr = allQR.find((qr) => qr.id === id);
        if (qr) {
          setCurrentQR(qr);
          setPutQREnabled(true);
          const entity = ref.current?.cesiumElement?.entities.getById(qr.id);
          if (entity) {
            ref.current?.cesiumElement?.flyTo(entity);
          }
        }
      }
    } else if (isPOI) {
      if (allPOI && allPOI.length > 0 && id) {
        const poi = allPOI.find((poi) => poi.id === id);
        if (poi) {
          setCurrentPOI(poi);
          setPutPOIEnabled(true);
          const entity = ref.current?.cesiumElement?.entities.getById(poi.id);
          if (entity) {
            ref.current?.cesiumElement?.flyTo(entity);
          }
        }
      }
    }
  }, [allQR, allPOI, id]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      // Show currently placed POI
      if (currentPOI !== null) {
        const position = Cartesian3.fromDegrees(
          currentPOI.longitude,
          currentPOI.latitude,
          currentPOI.altitude
        );
        
        const orientation = Utils.calculatePOIOrientation(currentPOI);

        const entity = ref.current.cesiumElement.entities.getById(
          currentPOI.id
        );

        if (!entity) {
          // Add new POI
          const entity = ref.current.cesiumElement.entities.add({
            id: currentPOI.id,
            name: currentPOI.id,
            position: position,
            box: {
              dimensions: new Cartesian3(30, 20, 10),
              material: Color.RED.withAlpha(0.75),
            },
            //orientation: Transforms.headingPitchRollQuaternion(position, hqr),
            orientation: orientation,
          });

          if (entity) ref.current?.cesiumElement?.flyTo(entity);
        } else {
          // Update POI
          if (prevPOI.current && !_.isEqual(currentPOI, prevPOI.current)) {
            if (currentPOI.modelUrl !== prevPOI.current.modelUrl) {
              // If modelUrl changed, remove old model and add new one
              ref.current?.cesiumElement?.entities.removeById(currentPOI.id);
              const newEntity = ref.current.cesiumElement.entities.add({
                id: currentPOI.id,
                name: currentPOI.id,
                position: position,
                model: {
                  uri: currentPOI.modelUrl,
                },
                //orientation: Transforms.headingPitchRollQuaternion(position,hqr),
                orientation: orientation,
              });
            }

            updatePOIEntity(currentPOI, prevPOI.current);
          }
        }
      }
      /*
      if (prevPOI.current) {
        const exsists = allPOI?.find((poi) => poi.id === prevPOI.current?.id);

        if (exsists) {
          updatePOIEntity(exsists, prevPOI.current);
        } else {
          ref.current?.cesiumElement?.entities.removeById(prevPOI.current?.id);
        }
      }
*/
      prevPOI.current = structuredClone(currentPOI);
    }
  }, [currentPOI]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      console.log("currentQR updated");
      // Show currently placed POI
      if (currentQR !== null) {
        const position = Cartesian3.fromDegrees(
          currentQR.longitude,
          currentQR.latitude,
          currentQR.altitude
        );

        const hqr = new HeadingPitchRoll(
          CesiumMath.toRadians(currentQR.heading),
          0,
          0
        );

        const entity = ref.current.cesiumElement.entities.getById(currentQR.id);

        if (!entity) {
          // Add new QR
          const content = `{https://www.foundat.com/app,"ast":0,"la":${currentQR.latitude},"lo":${currentQR.longitude},"a":${currentQR.altitude},"h":${currentQR.heading}}`;
          QRCode.toDataURL(content).then((value) => {
            const entity = ref.current?.cesiumElement?.entities.add({
              id: currentQR.id,
              name: currentQR.id,
              position: Cartesian3.fromDegrees(
                currentQR.longitude,
                currentQR.latitude - QR_SPACE,
                currentQR.altitude
              ),
              plane: {
                plane: new Plane(Cartesian3.UNIT_Y, 0),
                dimensions: new Cartesian3(QR_SIZE, QR_SIZE, QR_SIZE),
                material: new ImageMaterialProperty({ image: value }),
              },
              orientation: Transforms.headingPitchRollQuaternion(position, hqr),
            });

            ref.current?.cesiumElement?.entities.add({
              id: "back-" + currentQR.id,
              name: currentQR.id,
              position: Cartesian3.fromDegrees(
                currentQR.longitude,
                currentQR.latitude + QR_SPACE,
                currentQR.altitude
              ),
              plane: {
                plane: new Plane(Cartesian3.UNIT_Y, 0),
                dimensions: new Cartesian3(QR_SIZE, QR_SIZE, QR_SIZE),
                material: Color.WHITE,
              },
              orientation: Transforms.headingPitchRollQuaternion(position, hqr),
            });

            if (entity) ref.current?.cesiumElement?.flyTo(entity);
          });
        } else {
          // Update RQ
          if (prevQR.current && !_.isEqual(currentQR, prevQR.current)) {
            updateQREntity(currentQR, prevQR.current);
          }
        }
      }
      /*
        if (prevQR.current) {
          const exsists = allQR?.find((qr) => qr.id === prevQR.current?.id);
  
          if (exsists) {
            updateQREntity(exsists, prevQR.current);
          } else {
            ref.current.cesiumElement.entities.removeById(prevQR.current.id);
            ref.current.cesiumElement.entities.removeById(
              "back-" + prevQR.current.id
            );
          }
        }
        */
      prevQR.current = currentQR;
    }
  }, [currentQR]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      // List all saved POIs
      if (allPOI !== null) {
        // Remove deleted POI
        if (prevAllPOI.current) {
          const set = new Set(allPOI.map((poi) => poi.id));
          const removed = prevAllPOI.current.filter((poi) => !set.has(poi.id));
          if (removed.length > 0) {
            removed.forEach((poi) =>
              ref.current?.cesiumElement?.entities.removeById(poi.id)
            );
          }
        }

        allPOI.forEach((poi) => {
          const entity = ref.current?.cesiumElement?.entities.getById(poi.id);
          // Add new POI
          if (!entity) {
            const position = Cartesian3.fromDegrees(
              poi.longitude,
              poi.latitude,
              poi.altitude
            );
            
            const orientation = Utils.calculatePOIOrientation(poi);

            if (poi.modelUrl) {
              ref.current?.cesiumElement?.entities.add({
                id: poi.id,
                name: poi.id,
                position: position,
                model: {
                  uri: poi.modelUrl,
                  minimumPixelSize: POI_SIZE,
                  maximumScale: 256,
                },
                //orientation: Transforms.headingPitchRollQuaternion(position,hqr),
                orientation: orientation,
              });
            } else {
              ref.current?.cesiumElement?.entities.add({
                id: poi.id,
                name: poi.id,
                position: position,
                box: {
                  dimensions: new Cartesian3(POI_SIZE, POI_SIZE, POI_SIZE),
                  material: Color.RED.withAlpha(0.75),
                },
                //orientation: Transforms.headingPitchRollQuaternion(position,hqr),
                orientation: orientation,
              });
            }
          } else {
            if (prevAllPOI.current) {
              const prev = prevAllPOI.current.find(
                (prev) => prev.id === poi.id
              );
              if (prev && !_.isEqual(poi, prev)) updatePOIEntity(poi, prev);
            }
          }
        });
      }

      prevAllPOI.current = structuredClone(allPOI);
      /*
      if (allPOI && allPOI.length > 0) {
        const poi = allPOI[0];
        const entity = ref.current?.cesiumElement?.entities.getById(poi.id);
        if (entity) {
          ref.current?.cesiumElement?.flyTo(entity);
        }
      }
        */
    }
  }, [allPOI]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      // List all saved QRs
      if (allQR !== null) {
        // Remove deleted QR
        if (prevAllQR.current) {
          const set = new Set(allQR.map((qr) => qr.id));
          const removed = prevAllQR.current.filter((qr) => !set.has(qr.id));
          if (removed.length > 0) {
            removed.forEach((qr) => {
              ref.current?.cesiumElement?.entities.removeById(qr.id);
              ref.current?.cesiumElement?.entities.removeById("back-" + qr.id);
            });
          }
        }

        allQR.forEach((qr) => {
          const entity = ref.current?.cesiumElement?.entities.getById(qr.id);
          // Add new QR
          if (!entity) {
            const position = Cartesian3.fromDegrees(
              qr.longitude,
              qr.latitude,
              qr.altitude
            );

            const hqr = new HeadingPitchRoll(
              CesiumMath.toRadians(qr.heading),
              0,
              0
            );

            // Add new QR
            const content = `{https://www.foundat.com/app,"ast":0,"la":${qr.latitude},"lo":${qr.longitude},"a":${qr.altitude},"h":${qr.heading}}`;
            QRCode.toDataURL(content).then((value) => {
              ref.current?.cesiumElement?.entities.add({
                id: qr.id,
                name: qr.id,
                position: Cartesian3.fromDegrees(
                  qr.longitude,
                  qr.latitude - QR_SPACE,
                  qr.altitude
                ),
                plane: {
                  plane: new Plane(Cartesian3.UNIT_Y, 0),
                  dimensions: new Cartesian3(QR_SIZE, QR_SIZE, QR_SIZE),
                  material: new ImageMaterialProperty({ image: value }),
                },
                orientation: Transforms.headingPitchRollQuaternion(
                  position,
                  hqr
                ),
              });

              ref.current?.cesiumElement?.entities.add({
                id: "back-" + qr.id,
                name: qr.id,
                position: Cartesian3.fromDegrees(
                  qr.longitude,
                  qr.latitude + QR_SPACE,
                  qr.altitude
                ),
                plane: {
                  plane: new Plane(Cartesian3.UNIT_Y, 0),
                  dimensions: new Cartesian3(QR_SIZE, QR_SIZE, QR_SIZE),
                  material: Color.WHITE,
                },
                orientation: Transforms.headingPitchRollQuaternion(
                  position,
                  hqr
                ),
              });
            });
          } else {
            if (prevAllQR.current) {
              const prev = prevAllQR.current.find((prev) => prev.id === qr.id);
              if (prev && !_.isEqual(qr, prev)) updateQREntity(qr, prev);
            }
          }
        });
      }

      prevAllQR.current = allQR;
    }
  }, [allQR]);

  const initMap = async () => {
    if (ref.current && ref.current.cesiumElement) {
      ref.current.cesiumElement.scene.setTerrain(
        Terrain.fromWorldTerrain({
          requestWaterMask: true,
          requestVertexNormals: true,
        })
      );
    }
  };

  const getCurrentPosition = async () => {
    if (ref.current && ref.current.cesiumElement) {
      ref.current.cesiumElement.camera.flyTo({
        destination: Cartesian3.fromDegrees(
          context.currentPosition.longitude,
          context.currentPosition.latitude,
          context.currentPosition.altitude || 12000
        ),
        duration: 4,
      });
    }
  };

  const fetchData = async () => {
    const poiService = new POIsV1Service();
    const response = await poiService.getPOIs();

    setAllPOI(response.data);

    const qrCodeService = new QrCodesV1Service();
    const qr_response = await qrCodeService.getQrCodes();

    const qrs: IQrCode[] = await qr_response.data;
    setAllQR(qrs);
  };

  const updateQREntity = (qr: IQrCode, prev: IQrCode) => {
    if (ref.current && ref.current.cesiumElement) {
      const entity = ref.current.cesiumElement.entities.getById(qr.id);
      const back = ref.current.cesiumElement.entities.getById("back-" + qr.id);

      if (entity && back) {
        if (prev && !_.isEqual(qr, prev)) {
          // Change position
          if (
            prev.latitude !== qr.latitude ||
            prev.longitude !== qr.longitude ||
            prev.altitude !== qr.altitude ||
            prev.heading !== qr.heading
          ) {
            entity.position = new CallbackPositionProperty(
              () =>
                Cartesian3.fromDegrees(
                  qr.longitude -
                    Math.sin(CesiumMath.toRadians(qr.heading)) * QR_SPACE,
                  qr.latitude -
                    Math.cos(CesiumMath.toRadians(qr.heading)) * QR_SPACE,
                  qr.altitude
                ),
              false
            );
            back.position = new CallbackPositionProperty(
              () =>
                Cartesian3.fromDegrees(
                  qr.longitude +
                    Math.sin(CesiumMath.toRadians(qr.heading)) * QR_SPACE,
                  qr.latitude +
                    Math.cos(CesiumMath.toRadians(qr.heading)) * QR_SPACE,
                  qr.altitude
                ),
              false
            );

            const position = Cartesian3.fromDegrees(
              qr.longitude,
              qr.latitude,
              qr.altitude
            );

            const hqr = new HeadingPitchRoll(
              CesiumMath.toRadians(qr.heading),
              0,
              0
            );

            entity.orientation = new CallbackProperty(
              () => Transforms.headingPitchRollQuaternion(position, hqr),
              false
            );
            back.orientation = new CallbackProperty(
              () => Transforms.headingPitchRollQuaternion(position, hqr),
              false
            );
          }
        }
      }
    }
  };

  const updatePOIEntity = (poi: IPOI, prev: IPOI) => {
    if (ref.current && ref.current.cesiumElement) {
      const entity = ref.current.cesiumElement.entities.getById(poi.id);

      if (entity) {
        const position = Cartesian3.fromDegrees(
          poi.longitude,
          poi.latitude,
          poi.altitude
        );

        if (
          poi.latitude !== prev.latitude ||
          poi.longitude !== prev.longitude ||
          poi.altitude !== prev.altitude
        ) {
          entity.position = new CallbackPositionProperty(() => position, false);
        }

        //if (poi.heading !== prev.heading) {
        if (
          poi.heading !== prev.heading ||
          poi.flipX !== prev.flipX ||
          poi.flipY !== prev.flipY ||
          poi.flipZ !== prev.flipZ
        ) {
          
          const orientation = Utils.calculatePOIOrientation(poi);
          entity.orientation = new CallbackProperty(() => orientation, false);
        }
      }
    }
  };

  const clickEvent = (e: any, target: any) => {
    if (ref.current && ref.current.cesiumElement) {
      if (target?.id instanceof Entity) {
        ref.current.cesiumElement.selectedEntity = target?.id;

        if (putQREnabled && target.primitive instanceof Model) {
          const cartesian = ref.current.cesiumElement.scene.pickPosition(
            e.position
          );

          if (cartesian) {
            const cartographic: Cartographic =
              Cartographic.fromCartesian(cartesian);
            const longitude: number = CesiumMath.toDegrees(
              cartographic.longitude
            );
            const latitude: number = CesiumMath.toDegrees(
              cartographic.latitude
            );
            const height: number = cartographic.height;

            const uuid = uuidv4();
            setCurrentQR({
              id: uuid,
              latitude: latitude,
              longitude: longitude,
              heading: 0,
              altitude: height,
              created: new Date(),
              description: "",
              //content: BASE_URL + "#" + uuid,
            });

            setPutQREnabled(false);
          }
        }
        return;
      } else if (putPOIEnabled && e && e.position) {
        const cartesian = ref.current?.cesiumElement?.scene.pickPosition(
          e.position
        );

        if (cartesian) {
          const cartographic: Cartographic =
            Cartographic.fromCartesian(cartesian);
          const longitude: number = CesiumMath.toDegrees(
            cartographic.longitude
          );
          const latitude: number = CesiumMath.toDegrees(cartographic.latitude);
          const height: number = cartographic.height + 5;

          const uuid = uuidv4();
          setCurrentPOI({
            id: uuid,
            latitude: latitude,
            longitude: longitude,
            heading: 0,
            altitude: height,
            modelUrl: "",
          });

          setPutPOIEnabled(false);
        }
      }
    }
  };

  const selectedEntityChangeEvent = (entity: Entity) => {
    if (entity) {
      const poi = allPOI?.find((poi) => poi.id === entity.id);
      if (poi) {
        setCurrentPOI(poi);
        setCurrentQR(null);
      }

      const qr = allQR?.find((qr) => qr.id === entity.id);
      if (qr) {
        setCurrentQR(qr);
        setCurrentPOI(null);
      }
    }
  };

  const onChangeModelFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setPOIFile(file);
    }
  };

  const updatePOILatitude = (amount: number) => {
    if (currentPOI) {
      const clone = structuredClone(currentPOI);
      clone.latitude += amount;
      setCurrentPOI(clone);
    }
  };

  const updatePOILongitude = (amount: number) => {
    if (currentPOI) {
      const clone = structuredClone(currentPOI);
      clone.longitude += amount;
      setCurrentPOI(clone);
    }
  };

  const updatePOIAltitude = (amount: number) => {
    if (currentPOI) {
      const clone = structuredClone(currentPOI);
      clone.altitude += amount;
      setCurrentPOI(clone);
    }
  };

  const updatePOIHeading = (amount: number) => {
    if (currentPOI) {
      const clone = structuredClone(currentPOI);
      clone.heading += amount;
      if (clone.heading < 0) clone.heading += 360;
      if (clone.heading > 360) clone.heading -= 360;
      setCurrentPOI(clone);
    }
  };

  const updatePOIFlipX = (amount: number) => {
    if (currentPOI) {
      const clone = structuredClone(currentPOI);
      if (clone.flipX === undefined) clone.flipX = 0;
      clone.flipX += amount;
      if (clone.flipX < 0) clone.flipX += 360;
      if (clone.flipX > 360) clone.flipX -= 360;
      setCurrentPOI(clone);
    }
  };

  const updatePOIFlipY = (amount: number) => {
    if (currentPOI) {
      const clone = structuredClone(currentPOI);
      if (clone.flipY === undefined) clone.flipY = 0;
      clone.flipY += amount;
      if (clone.flipY < 0) clone.flipY += 360;
      if (clone.flipY > 360) clone.flipY -= 360;
      setCurrentPOI(clone);
    }
  };

  const updatePOIFlipZ = (amount: number) => {
    if (currentPOI) {
      const clone = structuredClone(currentPOI);
      if (clone.flipZ === undefined) clone.flipZ = 0;
      clone.flipZ += amount;
      if (clone.flipZ < 0) clone.flipZ += 360;
      if (clone.flipZ > 360) clone.flipZ -= 360;
      setCurrentPOI(clone);
    }
  };

  const savePOIEvent = async (name: string) => {
    if (currentPOI) {
      const currentPOIClone = structuredClone(currentPOI);
      currentPOIClone.name = name;
      setCurrentPOI(currentPOIClone);

      setIsPOISaving(true);
      if (allPOI && allPOI.length > 0) {
        const exsists = allPOI.find((el) => el.id === currentPOIClone.id);

        if (!exsists) {
          // Add new POI data
          apiPostPOI(currentPOIClone)
            .then((data) => {
              console.log(data);

              const all = [...allPOI, currentPOIClone];
              setAllPOI(all);

              setCurrentPOI(data);
            })
            .catch((error) => console.error(error))
            .finally(() => {
              setIsPOISaving(false);
              setPOIFile(null);
              setShowPOISavingForm(false);
            });
        } else if (!_.isEqual(exsists, currentPOIClone)) {
          // Update POI data
          apiPutPOI(currentPOIClone)
            .then((data) => {
              console.log(data);

              const all = allPOI.map((data) =>
                data.id === currentPOIClone.id ? currentPOIClone : data
              );
              setAllPOI(all);

              setCurrentPOI(data);
            })
            .catch((error) => console.error(error))
            .finally(() => {
              setIsPOISaving(false);
              setPOIFile(null);
              setShowPOISavingForm(false);
            });
        } else {
          setCurrentPOI(null);
          setIsPOISaving(false);
          setShowPOISavingForm(false);
        }
      } else {
        // Add new POI data
        apiPostPOI(currentPOIClone)
          .then((data) => {
            console.log(data);

            const all = [currentPOIClone];
            setAllPOI(all);

            setCurrentPOI(data);
          })
          .catch((error) => console.error(error))
          .finally(() => {
            setIsPOISaving(false);
            setShowPOISavingForm(false);
          });
      }
    }
  };

  const cancelPOIEvent = () => {
    setCurrentPOI(null);
    setShowPOISavingForm(false);
  };

  const apiPostPOI = async (poi: IPOI): Promise<any> => {
    try {
      const poiService = new POIsV1Service();
      if (POIfile) {
        var url = await poiService.uploadFile(POIfile);
        if (url) {
          poi.modelUrl = url;
        }
      }
      const response = await poiService.create(poi);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const apiPutPOI = async (poi: IPOI) => {
    try {
      const poiService = new POIsV1Service();
      const response = await poiService.update(poi.id, poi);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const rotationStartEvent = () => {
    if (currentPOI) {
      setPutPOIEnabled(false);
      setRotatePOIEnabled(true);
      if (ref.current && ref.current.cesiumElement) {
        ref.current.cesiumElement.scene.screenSpaceCameraController.enableRotate =
          false;
        ref.current.cesiumElement.scene.screenSpaceCameraController.enableTranslate =
          false;
        ref.current.cesiumElement.scene.screenSpaceCameraController.enableZoom =
          false;
        ref.current.cesiumElement.scene.screenSpaceCameraController.enableTilt =
          false;
        ref.current.cesiumElement.scene.screenSpaceCameraController.enableLook =
          false;
      }
    }
  };

  const rotationEndEvent = () => {
    setRotatePOIEnabled(false);
    if (ref.current && ref.current.cesiumElement) {
      ref.current.cesiumElement.scene.screenSpaceCameraController.enableRotate =
        true;
      ref.current.cesiumElement.scene.screenSpaceCameraController.enableTranslate =
        true;
      ref.current.cesiumElement.scene.screenSpaceCameraController.enableZoom =
        true;
      ref.current.cesiumElement.scene.screenSpaceCameraController.enableTilt =
        true;
      ref.current.cesiumElement.scene.screenSpaceCameraController.enableLook =
        true;
    }
  };

  const onMouseDown = (e: any) => {
    if (ref.current && ref.current.cesiumElement) {
      if (rotatePOIEnabled) {
        rotationEndEvent();
      }
    }
  };

  const onMouseMove = (e: any) => {
    if (ref.current && ref.current.cesiumElement) {
      if (rotatePOIEnabled && currentPOI) {
        const position = Cartesian3.fromDegrees(
          currentPOI.longitude,
          currentPOI.latitude,
          currentPOI.altitude
        );

        const origin = SceneTransforms.worldToWindowCoordinates(
          ref.current.cesiumElement.scene,
          position
        );

        if (!origin) return;

        const deltaX = e.endPosition.x - origin.x;
        const deltaY = e.endPosition.y - origin.y;
        const angle = Math.atan2(deltaY, deltaX);

        let rotation = CesiumMath.toDegrees(angle);
        if (rotation < 0) rotation += 360;
        if (rotation > 360) rotation -= 360;

        const poi = structuredClone(currentPOI);
        poi.heading = rotation;
        setCurrentPOI(poi);
      }
    }
  };

  const updateQRLatitude = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.latitude += amount;
      setCurrentQR(clone);
    }
  };

  const updateQRLongitude = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.longitude += amount;
      setCurrentQR(clone);
    }
  };

  const updateQRAltitude = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.altitude += amount;
      setCurrentQR(clone);
    }
  };

  const updateQRHeading = (amount: number) => {
    if (currentQR) {
      const clone = structuredClone(currentQR);
      clone.heading += amount;
      if (clone.heading < 0) clone.heading = clone.heading + 360;
      if (clone.heading > 360) clone.heading = clone.heading - 360;
      setCurrentQR(clone);
    }
  };

  const saveQREvent = () => {
    if (currentQR) {
      const qrCodeService = new QrCodesV1Service();
      if (allQR && allQR.length > 0) {
        const exsists = allQR.find((el) => el.id === currentQR.id);

        if (!exsists) {
          qrCodeService
            .create(currentQR)
            .then(() => {
              qrCodeService
                .getQrCodes()
                .then((data) => {
                  setAllQR(data.data);
                  setCurrentQR(null);
                })
                .catch((error) => console.error(error));
            })
            .catch((error) => console.error(error));
        } else if (!_.isEqual(exsists, currentQR)) {
          qrCodeService
            .update(currentQR.id, currentQR)
            .then(() => {
              qrCodeService
                .getQrCodes()
                .then((data) => {
                  setAllQR(data.data);
                  setCurrentQR(null);
                })
                .catch((error) => console.error(error));
            })
            .catch((error) => console.error(error));
        } else {
          setCurrentQR(null);
        }
      } else {
        // Add new QR data
        qrCodeService
          .create(currentQR)
          .then(() => {
            qrCodeService
              .getQrCodes()
              .then((data) => {
                setAllQR(data.data);
                setCurrentQR(null);
              })
              .catch((error) => console.error(error));
          })
          .catch((error) => console.error(error));
      }
    }
  };

  const cancelQREvent = () => {
    setCurrentQR(null);
  };

  return (
    <>
      <div
        className="fixed top-20 left-4 overflow-x-hidden p-4 w-[300px] bg-slate-800 text-white"
        style={{ zIndex: 10 }}
      >
        <POIPanel
          currentPOI={currentPOI ?? undefined}
          updatePOIAltitude={updatePOIAltitude}
          updatePOIHeading={updatePOIHeading}
          updatePOILatitude={updatePOILatitude}
          updatePOILongitude={updatePOILongitude}
          updatePOIFlipX={updatePOIFlipX}
          updatePOIFlipY={updatePOIFlipY}
          updatePOIFlipZ={updatePOIFlipZ}
          savePOIEvent={() => {
            setShowPOISavingForm(true);
          }}
          cancelPOIEvent={cancelPOIEvent}
          isPOISaving={isPOISaving}
          setPutPOIEnabled={setPutPOIEnabled}
          onChangeModelFile={onChangeModelFile}
          rotationStartEvent={rotationStartEvent}
          isPutPOIEnabled={putPOIEnabled}
          setRotatePOIEnabled={setRotatePOIEnabled}
          rotatePOIEnabled={rotatePOIEnabled}
        ></POIPanel>
      </div>
      <div
        className="fixed top-20 right-4 overflow-x-hidden p-4 w-[300px] bg-slate-800 text-white"
        style={{ zIndex: 10 }}
      >
        <QRCodePanel
          currentQR={currentQR ?? undefined}
          updateQRLongitude={updateQRLongitude}
          updateQRLatitude={updateQRLatitude}
          updateQRAltitude={updateQRAltitude}
          updateQRHeading={updateQRHeading}
          saveQREvent={saveQREvent}
          cancelQREvent={cancelQREvent}
          setPutQREnabled={setPutQREnabled}
        ></QRCodePanel>
      </div>
      <Viewer
        ref={ref}
        animation={false}
        timeline={false}
        homeButton={true}
        sceneModePicker={false}
        baseLayerPicker={true}
        navigationHelpButton={false}
        shouldAnimate={true}
        requestRenderMode={true}
        infoBox={true}
        extend={viewerDragDropMixin}
        geocoder={IonGeocodeProviderType.GOOGLE}
        onClick={(e, target) => clickEvent(e, target)}
        onSelectedEntityChange={(entity) => selectedEntityChangeEvent(entity)}
      >
        <Cesium3DTileset
          url={`https://tile.googleapis.com/v1/3dtiles/root.json?key=${environment.Google_Api_Key}`}
          showCreditsOnScreen={false}
          skipLevelOfDetail={true}
        ></Cesium3DTileset>
        <ScreenSpaceEventHandler>
          <ScreenSpaceEvent
            action={(e) => onMouseDown(e)}
            type={ScreenSpaceEventType.LEFT_DOWN}
          />
          <ScreenSpaceEvent
            action={(e) => onMouseMove(e)}
            type={ScreenSpaceEventType.MOUSE_MOVE}
          />
        </ScreenSpaceEventHandler>
      </Viewer>
      <ObjectForm
        onSave={savePOIEvent}
        onCancel={cancelPOIEvent}
        show={showPOISavingForm}
        isSaving={isPOISaving}
        name={currentPOI?.name ?? ""}
      />
    </>
  );
};
